import React, { useState, useEffect, useCallback } from "react";
import { post, put } from "./utils/fetch";
import { ckeckAccessTokenExpire } from "./utils/authorize";
import { User, Group } from "./utils/types";
import {
  API_ENDPOINT,
  APP_ENDPOINT,
  APPLICATION_NAME,
  DIRECTORY_SCOPES,
  FLOW_NAME,
  KEY_G_SUITE_ACCESS_TOKEN,
  SI_API_ENDPOINT,
} from "./utils/constants";
import { useScopes, useAccessToken } from "./Verify";
import { AfterInstruction } from "./Manual";
import { parse } from "url";
import SyncForm from "./components/pages/SyncForm";
import { ErrorModal, InfoModal } from "./AppModal";
import Loader from "react-loader";
import { useHistory } from "react-router-dom";

function Sync() {
  const history = useHistory();
  const [enableDelete, setEnableDelete] = useState<boolean>(false);

  const {
    gSuiteAccessToken,
    loading: gSuiteAccessTokenLoading,
    error: gSuiteAccessTokenError,
  } = useGSuiteAccessToken();
  const {
    accessToken,
    loading: accessTokenLoading,
    error: accessTokenError,
  } = useAccessToken(`${APP_ENDPOINT}/sync`, gSuiteAccessToken !== "");
  const { scopes, loading: scopesLoading, error: scopesTokenError } = useScopes(
    accessToken,
  );
  const {
    loading: upsertScopeLoading,
    error: upsertScopeError,
  } = useUpsertOAuth(gSuiteAccessToken, scopes);
  const { groups, loading: groupsLoading, error: groupsError } = useGroups(
    accessToken,
  );
  const {
    users,
    currentGroup,
    currentQuery,
    doFetchUsers,
    doSynchronize,
    loading: usersLoading,
    error: usersError,
    info,
    initInfo,
  } = useUsers(accessToken, gSuiteAccessToken, scopes, enableDelete);

  const onGotoSetting = () => {
    history.push("/schedule", { accessToken });
  };

  const loading =
    gSuiteAccessTokenLoading ||
    accessTokenLoading ||
    scopesLoading ||
    upsertScopeLoading ||
    groupsLoading ||
    usersLoading;
  const error =
    gSuiteAccessTokenError ||
    accessTokenError ||
    scopesTokenError ||
    upsertScopeError ||
    groupsError ||
    usersError;

  const userButtonDisable = loading || error !== "";
  const syncButtonDisable = loading || error !== "" || users.length === 0;
  const goToScheduleButtonDisable =
    loading || gSuiteAccessTokenError !== "" || accessTokenError !== "";

  useEffect(() => {
    if (currentGroup !== "All" || currentQuery !== "") {
      setEnableDelete(false);
    }
  }, [currentGroup, currentQuery]);

  return (
    <>
      <SyncForm
        groups={groups}
        users={users}
        currentGroup={currentGroup}
        currentQuery={currentQuery}
        enableDelete={enableDelete}
        onForward={onGotoSetting}
        doFetchUsers={doFetchUsers}
        doSynchronize={doSynchronize}
        doSelectEnableDelete={setEnableDelete}
        userButtonDisable={userButtonDisable}
        syncButtonDisable={syncButtonDisable}
        forwardButtonDisable={goToScheduleButtonDisable}
      />
      <Loader loaded={!loading} className="react-loader" />
      <InfoModal
        show={info !== ""}
        body={info}
        callback={() => {
          initInfo();
        }}
      />
      <ErrorModal show={error !== ""} body={error} callback={() => {}} />
    </>
  );
}

export function useGSuiteAccessToken() {
  const [gSuiteAccessToken, setGSuiteAccessToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | JSX.Element>("");

  useEffect(() => {
    fetchGSuiteAccessToken();
  }, []);

  const fetchGSuiteAccessToken = async () => {
    try {
      setGSuiteAccessToken(
        ckeckAccessTokenExpire(
          window.localStorage.getItem(KEY_G_SUITE_ACCESS_TOKEN),
        ),
      );
    } catch (e) {
      try {
        const { query } = parse(window.location.href, true);
        if (query.error) {
          switch (query.error) {
            case "error.wrong_mail":
              setError(
                <div>
                  <h4>It may correspond to any of the following:</h4>
                  <ul>
                    <li>
                      E-mail address is not Google Workspace's e-mail address
                    </li>
                    <li>
                      E-mail address is not Google Workspace domain
                      administrator
                    </li>
                    <li>
                      The Google Workspace domain to which you belong does not
                      allow RICOH Smart Integration to access user data in the
                      domain
                    </li>
                  </ul>
                </div>,
              );
              return;
            case "error.revoked_or_expired":
              setError(
                "The URL you clicked has expired or has been revoked. Please send confirm Google Workspace admin mail again",
              );
              return;
            default:
              setError(JSON.stringify(query.error));
              return;
          }
        }
        if (
          !query.gsuiteDomainAdminExchangeCode ||
          typeof query.gsuiteDomainAdminExchangeCode !== "string"
        ) {
          setError("Please click the URL which send your E-mail address.");
          return;
        }
        const { gSuiteAccessToken, expiresIn } = await getGSuiteAccessToken(
          query.gsuiteDomainAdminExchangeCode,
        );
        window.localStorage.setItem(
          KEY_G_SUITE_ACCESS_TOKEN,
          JSON.stringify({
            expire: new Date().getTime() + expiresIn * 1000,
            value: gSuiteAccessToken,
          }),
        );
        setGSuiteAccessToken(gSuiteAccessToken);
      } catch (e) {
        setError("useGSuiteAccessToken:" + JSON.stringify(e.message));
      }
    }
    setLoading(false);
  };

  return { gSuiteAccessToken, loading, error };
}

export function useUpsertOAuth(gSuiteAccessToken: string, scopes: string[]) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchUpsertOAuth();
  }, [gSuiteAccessToken, scopes]);

  const fetchUpsertOAuth = async () => {
    if (!gSuiteAccessToken || scopes.length === 0) {
      return;
    }
    try {
      setLoading(true);
      await upsertOAuth(gSuiteAccessToken, [...scopes, ...DIRECTORY_SCOPES]);
    } catch (e) {
      setError("useUpsertOAuth: " + JSON.stringify(e.message));
    }
    setLoading(false);
  };

  return { loading, error };
}

export function useGroups(accessToken: string) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchGroups();
  }, [accessToken]);

  const fetchGroups = async () => {
    if (!accessToken) {
      return;
    }
    try {
      setLoading(true);
      setGroups(await getGroups(accessToken));
    } catch (e) {
      setError("useGroups: " + JSON.stringify(e.message));
    }
    setLoading(false);
    return;
  };

  return { groups, loading, error };
}

export function useUsers(
  accessToken: string,
  gSuiteAccessToken: string,
  scopes: string[],
  enableDelete: boolean,
) {
  const [users, setUsers] = useState<User[]>([]);
  const [currentGroup, setCurrentGroup] = useState("");
  const [currentQuery, setCurrentQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState<string | JSX.Element>("");

  const doFetchUsers = useCallback((group: string, query: string) => {
    setCurrentGroup(group);
    setCurrentQuery(query);
  }, []);
  const doSynchronize = useCallback(() => {
    (async () => {
      if (currentGroup === "") {
        return;
      }
      setLoading(true);
      try {
        await synchronizeUsers(
          accessToken,
          gSuiteAccessToken,
          currentGroup,
          currentQuery,
          scopes,
          enableDelete,
        );
        setInfo(AfterInstruction());
      } catch (e) {
        setError("doSynchronize: " + JSON.stringify(e.message));
      }
      setLoading(false);
    })();
  }, [currentGroup, currentQuery, scopes, enableDelete]);

  useEffect(() => {
    fetchUsers();
  }, [currentGroup, currentQuery]);

  const fetchUsers = async () => {
    if (currentGroup === "") {
      return;
    }
    setLoading(true);
    try {
      setUsers(await getUsers(accessToken, currentGroup, currentQuery));
    } catch (e) {
      setError("fetchUsers: " + JSON.stringify(e.message));
    }
    setLoading(false);
  };
  const initInfo = () => {
    setInfo("");
  };

  return {
    users,
    currentGroup,
    currentQuery,
    doFetchUsers,
    doSynchronize,
    loading,
    error,
    info,
    initInfo,
  };
}

const getGSuiteAccessToken = async (exchangeCode: string) => {
  const responseJson = await post(
    `${API_ENDPOINT}/v1/aut/oauth/provider/token/exchange`,
    {
      body: JSON.stringify({
        exchange_code: exchangeCode,
      }),
    },
  );
  if (!responseJson || !responseJson.access_token || !responseJson.expires_in) {
    throw Error(responseJson.error);
  }
  return {
    gSuiteAccessToken: responseJson.access_token,
    expiresIn: responseJson.expires_in,
  };
};

const upsertOAuth = async (
  gSuiteAccessToken: string,
  scopes: string[],
): Promise<void> => {
  const responseJson = await put(`${SI_API_ENDPOINT}/v1/aut/gsuiteUsers/me`, {
    headers: {
      Authorization: `Bearer ${gSuiteAccessToken}`,
    },
    body: JSON.stringify({
      extension: {
        scopes,
      },
    }),
  });
  if (responseJson.error) {
    throw Error(responseJson.error);
  }
};

const getGroups = async (accessToken: string): Promise<Group[]> => {
  const responseJson = await post(
    `${SI_API_ENDPOINT}/v1/io/applications/${APPLICATION_NAME}/flows/${FLOW_NAME}/jobs`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        parameters: {
          postCustomParameter_jobs: {
            text: "getGroups",
          },
          postCustomParameter_preview: {
            bool: true,
          },
        },
        sync: true,
        debugMode: true,
      }),
    },
  );
  if (!responseJson.groups) {
    throw Error(responseJson.error);
  }
  return responseJson.groups;
};

const getUsers = async (
  accessToken: string,
  currentGroup: string,
  currentQuery: string,
): Promise<User[]> => {
  const group = currentGroup === "All" ? null : currentGroup;
  const responseJson = await post(
    `${SI_API_ENDPOINT}/v1/io/applications/${APPLICATION_NAME}/flows/${FLOW_NAME}/jobs`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        parameters: {
          postCustomParameter_jobs: {
            text: "getUsers",
          },
          postCustomParameter_query: {
            text: currentQuery,
          },
          postCustomParameter_groupKey: {
            text: group,
          },
          postCustomParameter_preview: {
            bool: true,
          },
        },
        sync: true,
        debugMode: true,
      }),
    },
  );
  if (!responseJson[0]) {
    throw Error(responseJson.error);
  }
  return responseJson[0];
};

const synchronizeUsers = async (
  accessToken: string,
  gSuiteAccessToken: string,
  currentGroup: string,
  currentQuery: string,
  scopes: string[],
  enableDelete: boolean,
) => {
  const group = currentGroup === "All" ? null : currentGroup;
  const localStorageGSuiteAccessToken = window.localStorage.getItem(
    KEY_G_SUITE_ACCESS_TOKEN,
  );
  if (!localStorageGSuiteAccessToken) {
    throw Error("Please re-send e-mail and click the URL");
  }
  const responseJson = await post(
    `${SI_API_ENDPOINT}/v1/io/applications/${APPLICATION_NAME}/flows/${FLOW_NAME}/jobs`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        parameters: {
          postCustomParameter_jobs: {
            text: "getUsers",
          },
          postCustomParameter_query: {
            text: currentQuery,
          },
          postCustomParameter_groupKey: {
            text: group,
          },
          postCustomParameter_preview: {
            bool: false,
          },
          postCustomParameter_gSuiteAccessToken: {
            text: gSuiteAccessToken,
          },
          postCustomParameter_rsiAccessToken: {
            text: accessToken,
          },
          postCustomParameter_scopes: {
            multiple: scopes,
          },
          postCustomParameter_isDelete: {
            bool: enableDelete,
          },
        },
        sync: false,
        debugMode: true,
      }),
    },
  );
  if (responseJson.error) {
    throw Error(responseJson.error);
  }
  return responseJson;
};

export default Sync;
